import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  FileText, Send, Printer, Check, X,
  MessageCircle, Mail, Download, Eye,
  ArrowLeft, Plus, Share2
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'

import Button from '../components/ui/Button'
import { OrdemServico } from '../types'
import { formatValor } from '../services/ordensServico'
import ordensServicoService from '../services/ordensServico'
import { useToast } from '../components/Toast'
import apiService from '../services/api'

const OrdemServicoSuccessPage: React.FC = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { success, error, info } = useToast()
  const [sending, setSending] = useState(false)
  const [pdfUrl, setPdfUrl] = useState<string>('')

  // Buscar dados da OS
  const { data: ordemServico, isLoading } = useQuery({
    queryKey: ['ordem-servico', id],
    queryFn: () => ordensServicoService.getById(parseInt(id!)),
    enabled: !!id
  })

  // Gerar URL do PDF automaticamente ao carregar
  useEffect(() => {
    if (!ordemServico) return

    const token = localStorage.getItem('token')
    const url = `/api/ordens-servico/${ordemServico.id}/cupom/abertura?token=${token}`
    setPdfUrl(url)
  }, [ordemServico])

  const handleSendWhatsApp = async () => {
    if (!ordemServico) return

    try {
      setSending(true)
      const telefone = ordemServico.cliente.telefonePrincipal?.replace(/\D/g, '')
      if (!telefone) {
        error('Cliente não possui telefone cadastrado')
        return
      }

      // Primeiro gerar o PDF
      await ordensServicoService.gerarRelatorio(ordemServico.id, 'orcamento')

      const mensagem = `Olá ${ordemServico.cliente.nome},\n\nSua ordem de serviço foi aberta com sucesso!\n\n📋 *ORDEM DE SERVIÇO ${ordemServico.numero}*\n\n*Equipamento:* ${ordemServico.equipamento.modelo?.marca?.nome || ''} ${ordemServico.equipamento.modelo?.nome || 'N/A'}\n*Problema Relatado:* ${ordemServico.problemaRelatado}\n*Estimativa:* ${formatValor(ordemServico.valorOrcamento)}\n\nEm breve entraremos em contato com o diagnóstico.\n\nAtenciosamente,\nEquipe Técnica`

      const whatsappUrl = `https://wa.me/${telefone}?text=${encodeURIComponent(mensagem)}`
      window.open(whatsappUrl, '_blank')

      success('Mensagem WhatsApp preparada com sucesso')
      info('Anexe o PDF manualmente no WhatsApp')
    } catch (err) {
      error('Erro ao preparar WhatsApp')
    } finally {
      setSending(false)
    }
  }

  const handleSendEmail = async () => {
    if (!ordemServico) return

    try {
      setSending(true)

      if (!ordemServico.cliente.email) {
        error('Cliente não possui email cadastrado')
        return
      }

      // Preparar email com mailto
      const subject = `Ordem de Serviço ${ordemServico.numero}`
      const body = `Prezado(a) ${ordemServico.cliente.nome},

Confirmamos o recebimento do seu equipamento para reparo.

ORDEM DE SERVIÇO: ${ordemServico.numero}
Data: ${new Date(ordemServico.dataEntrada).toLocaleDateString('pt-BR')}

Equipamento: ${ordemServico.equipamento.modelo?.marca?.nome || ''} ${ordemServico.equipamento.modelo?.nome || 'N/A'}
Número de Série: ${ordemServico.equipamento.numeroSerie || 'N/A'}
Problema Relatado: ${ordemServico.problemaRelatado}

Estimativa Inicial: ${formatValor(ordemServico.valorOrcamento)}

Em anexo segue o PDF da ordem de serviço para seus registros.

Entraremos em contato assim que tivermos o diagnóstico completo.

Atenciosamente,
Equipe Técnica`

      const mailtoUrl = `mailto:${ordemServico.cliente.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
      window.open(mailtoUrl, '_blank')

      success('Email preparado com sucesso')
      info('Anexe o PDF manualmente no seu cliente de email')
    } catch (err) {
      error('Erro ao preparar email')
    } finally {
      setSending(false)
    }
  }

  const handlePrint = async () => {
    if (!ordemServico || !pdfUrl) return

    try {
      // Abrir PDF em nova janela para impressão
      const printWindow = window.open(pdfUrl, '_blank')
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print()
        }
        success('Cupom PDF aberto para impressão')
      } else {
        error('Bloqueador de pop-up impediu a abertura do PDF')
      }
    } catch (err) {
      console.error('Erro ao imprimir cupom:', err)
      error('Erro ao imprimir cupom PDF')
    }
  }

  const handleDownloadCupom = async () => {
    if (!ordemServico) return

    try {
      await apiService.generateOrdemServicoPdf(ordemServico.id, 'ordem')
      success('Cupom PDF baixado com sucesso')
    } catch (err: any) {
      error(`Erro ao baixar cupom PDF: ${err.message}`)
    }
  }

  const handleNewOS = () => {
    navigate('/ordens-servico?new=true')
  }

  const handleGoToOS = () => {
    navigate('/ordens-servico')
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando ordem de serviço...</p>
        </div>
      </div>
    )
  }

  if (!ordemServico) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500">Ordem de serviço não encontrada</p>
          <Button
            className="mt-4"
            onClick={handleGoToOS}
            leftIcon={<ArrowLeft className="h-4 w-4" />}
          >
            Voltar
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header de Sucesso */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0">
              <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
                <Check className="h-6 w-6 text-green-600" />
              </div>
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900">
                Ordem de Serviço Criada com Sucesso!
              </h1>
              <p className="text-gray-600 mt-1">
                OS {ordemServico.numero} foi registrada e está pronta para ser enviada ao cliente
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleGoToOS}
                leftIcon={<ArrowLeft className="h-4 w-4" />}
              >
                Voltar à Lista
              </Button>
              <Button
                onClick={handleNewOS}
                leftIcon={<Plus className="h-4 w-4" />}
              >
                Nova OS
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Coluna Principal - Preview do PDF */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm">
              <div className="border-b border-gray-200 px-6 py-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Visualização do Documento
                  </h2>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleDownloadCupom}
                    leftIcon={<Download className="h-4 w-4" />}
                  >
                    Baixar Cupom
                  </Button>
                </div>
              </div>
              <div className="p-6">
                {/* Preview do Cupom PDF */}
                <div className="bg-gray-100 rounded-lg overflow-hidden" style={{ height: '800px' }}>
                  {pdfUrl ? (
                    <iframe
                      src={pdfUrl}
                      className="w-full h-full border-0"
                      title="Preview Cupom PDF"
                    />
                  ) : (
                    <div className="h-full flex items-center justify-center">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                        <p className="mt-2 text-gray-600">Gerando preview do cupom PDF...</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Coluna Lateral - Informações e Ações */}
          <div className="space-y-6">
            {/* Informações da OS */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Informações da OS
              </h3>
              <div className="space-y-3">
                <div>
                  <span className="text-xs font-medium text-gray-500 uppercase">Número</span>
                  <p className="text-sm font-semibold text-gray-900">{ordemServico.numero}</p>
                </div>
                <div>
                  <span className="text-xs font-medium text-gray-500 uppercase">Cliente</span>
                  <p className="text-sm text-gray-900">{ordemServico.cliente.nome}</p>
                  {ordemServico.cliente.telefonePrincipal && (
                    <p className="text-xs text-gray-500">{ordemServico.cliente.telefonePrincipal}</p>
                  )}
                </div>
                <div>
                  <span className="text-xs font-medium text-gray-500 uppercase">Equipamento</span>
                  <p className="text-sm text-gray-900">
                    {ordemServico.equipamento.modelo?.marca?.nome} {ordemServico.equipamento.modelo?.nome}
                  </p>
                  {ordemServico.equipamento.numeroSerie && (
                    <p className="text-xs text-gray-500">S/N: {ordemServico.equipamento.numeroSerie}</p>
                  )}
                </div>
                <div>
                  <span className="text-xs font-medium text-gray-500 uppercase">Problema</span>
                  <p className="text-sm text-gray-900">{ordemServico.problemaRelatado}</p>
                </div>
                <div>
                  <span className="text-xs font-medium text-gray-500 uppercase">Estimativa</span>
                  <p className="text-sm font-semibold text-gray-900">
                    {formatValor(ordemServico.valorOrcamento)}
                  </p>
                </div>
              </div>
            </div>

            {/* Ações de Envio */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Share2 className="h-5 w-5" />
                Enviar ao Cliente
              </h3>
              <div className="space-y-3">
                <Button
                  fullWidth
                  variant="outline"
                  onClick={handleSendWhatsApp}
                  disabled={sending || !ordemServico.cliente.telefonePrincipal}
                  leftIcon={<MessageCircle className="h-4 w-4" />}
                >
                  Enviar por WhatsApp
                </Button>
                <Button
                  fullWidth
                  variant="outline"
                  onClick={handleSendEmail}
                  disabled={sending || !ordemServico.cliente.email}
                  leftIcon={<Mail className="h-4 w-4" />}
                >
                  Enviar por Email
                </Button>
                <Button
                  fullWidth
                  variant="outline"
                  onClick={handlePrint}
                  leftIcon={<Printer className="h-4 w-4" />}
                >
                  Imprimir Cupom
                </Button>
              </div>

              {!ordemServico.cliente.telefonePrincipal && !ordemServico.cliente.email && (
                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-xs text-yellow-800">
                    ⚠️ Cliente não possui contatos cadastrados. Atualize o cadastro para habilitar o envio.
                  </p>
                </div>
              )}
            </div>

            {/* Dicas */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-blue-900 mb-2">
                💡 Dicas
              </h4>
              <ul className="text-xs text-blue-700 space-y-1">
                <li>• O cupom PDF inclui todos os dados da empresa e equipamento</li>
                <li>• Baixe o arquivo PDF para anexar em mensagens</li>
                <li>• Imprima diretamente em impressora térmica</li>
                <li>• Formato otimizado para papel térmico 80mm</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default OrdemServicoSuccessPage